/**
 * SSM-685 nav panel: vertical group tabs that swap the links column.
 *
 * Hovering a tab switches after a short intent delay, so a diagonal pass from
 * a tab to the links column doesn't flip the group on the way. Pointing at
 * anything other than a tab, or leaving the panel, cancels a pending switch.
 * Click and the arrow keys switch immediately. The panel resets to its first
 * group each time the pointer enters its top-level item.
 */
import { trapFocus } from '@theme/focus';
import { onAnimationEnd } from '@theme/utilities';

const INTENT_DELAY = 220;

class NavProofPanel extends HTMLElement {
  /** @type {number | undefined} */
  #timer;

  /** @type {HTMLElement | null} */
  #item = null;

  connectedCallback() {
    this.addEventListener('pointerover', this.#onPointerOver);
    this.addEventListener('pointerleave', this.#cancel);
    this.addEventListener('click', this.#onClick);
    this.addEventListener('keydown', this.#onKeyDown);
    this.#item = this.closest('.menu-list__list-item');
    this.#item?.addEventListener('pointerenter', this.#reset);
  }

  disconnectedCallback() {
    clearTimeout(this.#timer);
    this.removeEventListener('pointerover', this.#onPointerOver);
    this.removeEventListener('pointerleave', this.#cancel);
    this.removeEventListener('click', this.#onClick);
    this.removeEventListener('keydown', this.#onKeyDown);
    this.#item?.removeEventListener('pointerenter', this.#reset);
  }

  /** @returns {HTMLElement[]} */
  get tabs() {
    return Array.from(this.querySelectorAll('[role="tab"]'));
  }

  /**
   * @param {EventTarget | null} target
   * @returns {HTMLElement | null}
   */
  #tabFrom(target) {
    return target instanceof Element ? target.closest('[role="tab"]') : null;
  }

  /** @param {PointerEvent} event */
  #onPointerOver = (event) => {
    clearTimeout(this.#timer);
    const tab = this.#tabFrom(event.target);
    if (!tab || tab.getAttribute('aria-selected') === 'true') return;

    this.#timer = window.setTimeout(() => this.select(tab), INTENT_DELAY);
  };

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const tab = this.#tabFrom(event.target);
    if (!tab) return;

    clearTimeout(this.#timer);
    this.select(tab);
  };

  /** @param {KeyboardEvent} event */
  #onKeyDown = (event) => {
    const tab = this.#tabFrom(event.target);
    if (!tab) return;

    const tabs = this.tabs;
    const index = tabs.indexOf(tab);
    const last = tabs.length - 1;
    const next = {
      ArrowDown: index === last ? 0 : index + 1,
      ArrowUp: index === 0 ? last : index - 1,
      Home: 0,
      End: last,
    }[event.key];
    if (next === undefined) return;

    event.preventDefault();
    this.select(tabs[next], true);
  };

  #cancel = () => {
    clearTimeout(this.#timer);
  };

  #reset = () => {
    clearTimeout(this.#timer);
    const [first] = this.tabs;
    if (first) this.select(first);
  };

  /**
   * @param {HTMLElement} tab
   * @param {boolean} [focus]
   */
  select(tab, focus = false) {
    for (const other of this.tabs) {
      const selected = other === tab;
      other.setAttribute('aria-selected', String(selected));
      other.tabIndex = selected ? 0 : -1;

      const group = document.getElementById(other.getAttribute('aria-controls') ?? '');
      if (group) group.hidden = !selected;
    }
    if (focus) tab.focus();
  }
}

if (!customElements.get('nav-proof-panel')) {
  customElements.define('nav-proof-panel', NavProofPanel);
}

/**
 * Desktop menu after a narrow-then-wide resize. At 1200px or below the header
 * swaps in the drawer when the menu overflows, but it only swaps back from a
 * header height change (header.js), and widening the window doesn't change the
 * height, so the drawer stayed even at 1440px. Once the window is wider than
 * where the drawer took over, ask the header to bring the menu back; the
 * overflow list re-measures and swaps the drawer in again if it still doesn't
 * fit. Lives here rather than in header.js so the live header is untouched.
 */
let drawerShownAt = 0;
let resizeFrame = 0;

document.addEventListener('overflowMinimum', (event) => {
  if (!(event.target instanceof Element) || !event.target.closest('.header-menu--proof')) return;

  const { minimumReached } = /** @type {CustomEvent<{ minimumReached: boolean }>} */ (event).detail;
  drawerShownAt = minimumReached ? window.innerWidth : 0;
});

window.addEventListener('resize', () => {
  if (resizeFrame) return;

  resizeFrame = requestAnimationFrame(() => {
    resizeFrame = 0;
    if (!drawerShownAt || window.innerWidth <= drawerShownAt) return;

    document
      .querySelector('.header-menu--proof overflow-list')
      ?.dispatchEvent(new CustomEvent('overflowMinimum', { bubbles: true, detail: { minimumReached: false } }));
  });
});

/**
 * Drawer: going back from a group's links to the item's groups. The theme's
 * header-drawer handles one level of submenu, so its back() clears the
 * drawer's submenu-open state and traps focus on the whole drawer, leaving
 * Tab to walk the hidden first-level list. Once it finishes, restore both for
 * the item panel that is still open.
 */
document.addEventListener('click', (event) => {
  if (!(event.target instanceof Element)) return;

  const back = event.target.closest('.nav-proof-drawer__groups .menu-drawer__back-button');
  const groupSubmenu = back?.closest('.menu-drawer__submenu');
  const itemPanel = back?.closest('.nav-proof-drawer__groups')?.closest('details');
  const drawer = back?.closest('.menu-drawer');
  if (!groupSubmenu || !(itemPanel instanceof HTMLDetailsElement) || !drawer) return;

  onAnimationEnd(
    groupSubmenu,
    () =>
      requestAnimationFrame(() => {
        if (!itemPanel.open) return;
        drawer.classList.add('menu-drawer--has-submenu-opened');
        trapFocus(itemPanel);
      }),
    { subtree: false }
  );
});
